import { useState, useEffect, useCallback } from 'react';

export default function ExitIntentPopup() {
  const [visivel, setVisivel] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [erro, setErro] = useState('');

  const mostrarPopup = useCallback(() => {
    const jaExibido = sessionStorage.getItem('exitIntentExibido');
    if (!jaExibido) {
      setVisivel(true);
      sessionStorage.setItem('exitIntentExibido', 'true');
    }
  }, []);

  useEffect(() => {
    let timerInicial: ReturnType<typeof setTimeout>;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 10) {
        mostrarPopup();
      }
    };

    // Aguarda 4s antes de ativar o detector (não disparar imediatamente)
    timerInicial = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 4000);

    return () => {
      clearTimeout(timerInicial);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [mostrarPopup]);

  const fechar = () => setVisivel(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErro('');

    if (!nome.trim() || !email.trim()) {
      setErro('Preencha todos os campos para continuar.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErro('Digite um e-mail válido.');
      return;
    }

    setEnviando(true);

    try {
      const body = new URLSearchParams();
      body.append('nome', nome.trim());
      body.append('email', email.trim());
      body.append('origem', 'exit-intent-home');

      const resp = await fetch('https://readdy.ai/api/form/d6u57r29puk3bj9673v0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (resp.ok) {
        setEnviado(true);
      } else {
        setErro('Algo deu errado. Tente novamente.');
      }
    } catch {
      setErro('Erro de conexão. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  if (!visivel) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="relative w-full max-w-xl animate-[fadeInScale_0.35s_ease-out]"
        style={{ animation: 'fadeInScale 0.35s ease-out' }}
      >
        <style>{`
          @keyframes fadeInScale {
            from { opacity: 0; transform: scale(0.92) translateY(16px); }
            to   { opacity: 1; transform: scale(1)    translateY(0);    }
          }
        `}</style>

        {/* Card principal */}
        <div className="bg-[#0d0d0d] border border-gold-500/25 rounded-2xl overflow-hidden">
          {/* Imagem de fundo superior */}
          <div className="relative h-48 overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/4ebf795b193999efd329bca0b7d5992f.jpg"
              alt="Exercício gratuito PNL"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d]/30 via-transparent to-[#0d0d0d]" />

            {/* Badge topo */}
            <div className="absolute top-4 left-4 bg-gold-500/90 text-[#0a0a0a] text-[11px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full font-['Montserrat']">
              Espera! Presente Especial
            </div>

            {/* Botão fechar */}
            <button
              onClick={fechar}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-[#0d0d0d]/70 hover:bg-[#0d0d0d] rounded-full text-white/60 hover:text-white transition-colors cursor-pointer"
              aria-label="Fechar"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="px-8 pb-8 pt-2">
            {!enviado ? (
              <>
                <h2 className="font-display text-2xl font-bold text-white mb-2 leading-tight">
                  Antes de ir, leva um<br />
                  <span className="text-gold-400">exercício de PNL gratuito</span>
                </h2>
                <p className="text-white/45 text-sm font-['Montserrat'] mb-6 leading-relaxed">
                  Receba no seu e-mail o exercício de{' '}
                  <strong className="text-white/70">Ancoragem Emocional</strong> — o mesmo
                  usado por mais de 10.000 mulheres para quebrar padrões em 5 minutos.
                </p>

                {/* Benefícios rápidos */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    { icon: 'ri-time-line', texto: '5 min por dia' },
                    { icon: 'ri-shield-check-line', texto: '100% gratuito' },
                    { icon: 'ri-flashlight-line', texto: 'Resultado imediato' },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-gold-500/6 border border-gold-500/15 rounded-lg py-2.5 px-2 text-center"
                    >
                      <div className="w-5 h-5 flex items-center justify-center mx-auto mb-1">
                        <i className={`${item.icon} text-gold-400 text-sm`} />
                      </div>
                      <p className="text-white/50 text-[11px] font-['Montserrat']">{item.texto}</p>
                    </div>
                  ))}
                </div>

                <form data-readdy-form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <input
                      type="text"
                      name="nome"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      placeholder="Seu primeiro nome"
                      className="w-full bg-white/5 border border-white/10 focus:border-gold-500/50 rounded-lg px-4 py-3 text-white placeholder-white/25 text-sm font-['Montserrat'] outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Seu melhor e-mail"
                      className="w-full bg-white/5 border border-white/10 focus:border-gold-500/50 rounded-lg px-4 py-3 text-white placeholder-white/25 text-sm font-['Montserrat'] outline-none transition-colors"
                    />
                  </div>

                  {erro && (
                    <p className="text-red-400 text-xs font-['Montserrat']">{erro}</p>
                  )}

                  <button
                    type="submit"
                    disabled={enviando}
                    className="w-full py-3.5 bg-gradient-to-r from-gold-500 to-gold-700 hover:from-gold-400 hover:to-gold-600 text-[#0a0a0a] font-bold text-sm rounded-lg transition-all cursor-pointer disabled:opacity-60 font-['Montserrat'] tracking-wide whitespace-nowrap"
                  >
                    {enviando ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        Enviando...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-send-plane-fill" />
                        Quero meu exercício gratuito
                      </span>
                    )}
                  </button>

                  <p className="text-white/20 text-[11px] text-center font-['Montserrat']">
                    Sem spam. Cancele quando quiser.
                  </p>
                </form>

                <button
                  onClick={fechar}
                  className="w-full mt-3 text-white/25 hover:text-white/50 text-xs font-['Montserrat'] transition-colors cursor-pointer"
                >
                  Não, obrigada — vou sair sem o exercício
                </button>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gold-500/15 border border-gold-500/30 rounded-full">
                  <i className="ri-check-line text-gold-400 text-3xl" />
                </div>
                <h3 className="font-display text-2xl font-bold text-white mb-2">
                  Enviado com sucesso!
                </h3>
                <p className="text-white/45 text-sm font-['Montserrat'] mb-6">
                  Verifique seu e-mail — o exercício de Ancoragem Emocional está a caminho. Leva menos de 5 minutos e pode mudar seu dia completamente.
                </p>
                <button
                  onClick={fechar}
                  className="px-8 py-3 bg-gradient-to-r from-gold-500 to-gold-700 text-[#0a0a0a] font-bold text-sm rounded-lg cursor-pointer font-['Montserrat'] whitespace-nowrap"
                >
                  Continuar explorando
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
